// Validadores y formateadores de documentos uruguayos.

/* ───────── CI uruguaya ─────────
   Formato: 8 dígitos. El último es dígito verificador (algoritmo conocido).
   Ej: 4.123.456-7 → "41234567"
*/

export function limpiarCI(str) {
  return String(str || '').replace(/\D/g, '')
}

export function formatearCI(str) {
  const c = limpiarCI(str)
  // CIs viejas (< 1 millón): 7 dígitos → XXX.XXX-X
  // Ej: mamá de Nico "9762366" → "976.236-6"
  if (c.length === 7) {
    return c.slice(0, 3) + '.' + c.slice(3, 6) + '-' + c.slice(6)
  }
  // CIs modernas (>= 1 millón): 8 dígitos → X.XXX.XXX-X
  // Ej: Nico "36955508" → "3.695.550-8"
  if (c.length === 8) {
    return c.slice(0, 1) + '.' + c.slice(1, 4) + '.' + c.slice(4, 7) + '-' + c.slice(7)
  }
  return c  // Formato desconocido, devuelvo sin tocar
}

/** Calcula el dígito verificador de una CI uruguaya. */
function dvCI(ciSinDV) {
  const ci = ciSinDV.padStart(7, '0')
  const factores = [2, 9, 8, 7, 6, 3, 4]
  let suma = 0
  for (let i = 0; i < 7; i++) suma += parseInt(ci[i]) * factores[i]
  const resto = suma % 10
  return (10 - resto) % 10
}

export function validarCI(str) {
  const c = limpiarCI(str)
  // Solo chequeamos formato (7-8 dígitos). NO validamos dígito verificador
  // porque no tenemos base oficial y un falso positivo genera desconfianza.
  return c.length >= 7 && c.length <= 8
}

/* ───────── RUT uruguayo ─────────
   12 dígitos. Último es dígito verificador.
*/

export function limpiarRUT(str) {
  return String(str || '').replace(/\D/g, '')
}

function dvRUT(rutSinDV) {
  // RUT UY (12 dígitos): factores 2,3,4,5,6,7,2,3,4,5,6 de derecha a izquierda
  // sobre los 11 dígitos previos al DV.
  const factores = [2, 3, 4, 5, 6, 7, 2, 3, 4, 5, 6]
  let suma = 0
  for (let i = 0; i < 11; i++) {
    // dígito i desde la derecha
    const idx = 10 - i
    suma += parseInt(rutSinDV[idx] || '0') * factores[i]
  }
  const resto = suma % 11
  if (resto === 0) return 0
  return 11 - resto
}

export function validarRUT(str) {
  const r = limpiarRUT(str)
  // Solo formato (12 dígitos). NO validamos DV.
  return r.length === 12
}

/* ───────── Pasaporte ─────────
   Alfanumérico, mínimo 6 caracteres.
*/

export function limpiarPasaporte(str) {
  return String(str || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export function validarPasaporte(str) {
  const p = limpiarPasaporte(str)
  return p.length >= 6 && p.length <= 12
}

/* ───────── Fachada ───────── */

/** Valida según el tipo. Devuelve { ok, msg, normalizado } */
export function validarDocumento(tipo, valor) {
  if (!valor || !valor.trim()) return { ok: true, msg: '', normalizado: null } // vacío = OK (es opcional en algunos casos)
  switch (tipo) {
    case 'ci': {
      const limpio = limpiarCI(valor)
      if (!validarCI(limpio)) return { ok: false, msg: 'CI debe tener 7 u 8 dígitos', normalizado: limpio }
      return { ok: true, msg: '', normalizado: limpio }
    }
    case 'rut': {
      const limpio = limpiarRUT(valor)
      if (!validarRUT(limpio)) return { ok: false, msg: 'RUT debe tener 12 dígitos', normalizado: limpio }
      return { ok: true, msg: '', normalizado: limpio }
    }
    case 'pasaporte': {
      const limpio = limpiarPasaporte(valor)
      if (!validarPasaporte(limpio)) return { ok: false, msg: 'Pasaporte inválido (mínimo 6 caracteres)', normalizado: limpio }
      return { ok: true, msg: '', normalizado: limpio }
    }
    default:
      return { ok: false, msg: 'Tipo de documento inválido', normalizado: null }
  }
}

/* ───────── Email ───────── */

export function validarEmail(str) {
  if (!str?.trim()) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str.trim())
}

/* ───────── Teléfono UY ─────────
   Móviles: 9X XXX XXX (8 dígitos) o 09X XXX XXX (9 dígitos con 0 inicial)
   Fijos Mvd: 2 XXX XX XX (8 dígitos) o 02 XXX XX XX (9 con 0)
   Acepta también +598 al inicio. Espacios, guiones y puntos son ignorados.
*/

export function limpiarTel(str) {
  return String(str || '').replace(/\D/g, '')
}

/** Devuelve los dígitos "core" sin prefijo (598) ni "0" inicial. Ej: 098123456 → 98123456
 *  Fix 7-jul-2026 (Nico): soporta prefijo internacional 00 legacy (ej: contacto Lylian
 *  guardado como "00598 99 414 928" — común en imports desde iOS Contacts o vCards). */
function nucleoTel(str) {
  let t = limpiarTel(str)
  if (t.startsWith('00')) t = t.slice(2)  // prefijo internacional legacy 00598...
  if (t.startsWith('598')) t = t.slice(3)
  if (t.startsWith('0')) t = t.slice(1)  // descartar 0 inicial (formato local viejo)
  return t
}

export function validarTelUY(str) {
  const n = nucleoTel(str)
  // Móvil: 8 dígitos empezando con 9
  // Fijo MVD: 8 dígitos empezando con 2
  // Fijo interior: 7 dígitos empezando con 4 (ej: Maldonado 42...)
  if (n.length === 8 && /^[29]/.test(n)) return true
  if (n.length === 7 && /^4/.test(n)) return true
  return false
}

export function formatearTelUY(str) {
  const n = nucleoTel(str)
  if (n.length === 8 && n.startsWith('9')) {
    // Móvil: +598 9X XXX XXX
    return `+598 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`
  }
  if (n.length === 8 && n.startsWith('2')) {
    // Fijo Mvd: +598 2 XXX XX XX
    return `+598 2 ${n.slice(1, 4)} ${n.slice(4, 6)} ${n.slice(6)}`
  }
  if (n.length === 7 && n.startsWith('4')) {
    return `+598 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`
  }
  return str
}
